<template>
  <div class="site-navbar-wrap">
    <!-- Drawer overlay -->
    <div class="navbar-drawer-overlay" :class="{ 'navbar-drawer-overlay--visible': drawerOpen }" @click="closeDrawer"></div>

    <!-- Drawer -->
    <aside class="navbar-drawer" :class="{ 'navbar-drawer--open': drawerOpen }" aria-label="Meniu lateral">
      <div class="navbar-drawer__header">
        <span class="navbar-drawer__brand">{{ appName }}</span>
        <button type="button" class="navbar-drawer__close" @click="closeDrawer" aria-label="Închide meniu">
          ×
        </button>
      </div>

      <nav class="navbar-drawer__nav">
        <router-link class="navbar-drawer__link" :to="{ name: 'home' }" @click="closeDrawer">
          <span class="navbar-drawer__link-icon"><i class="mdi mdi-home" aria-hidden="true"></i></span>
          Acasă
        </router-link>

        <template v-if="currentUser">
          <div class="navbar-drawer__divider"></div>
          <div class="navbar-drawer__section-label">Contul meu</div>
          <router-link class="navbar-drawer__link" :to="{ name: 'profile' }" @click="closeDrawer">
            <span class="navbar-drawer__link-icon"><i class="mdi mdi-account" aria-hidden="true"></i></span>
            Profilul meu
          </router-link>
          <router-link class="navbar-drawer__link" :to="{ name: 'polls.create' }" @click="closeDrawer">
            <span class="navbar-drawer__link-icon"><i class="mdi mdi-chart-bar" aria-hidden="true"></i></span>
            Creare sondaj
          </router-link>
          <div class="navbar-drawer__divider"></div>
          <button type="button" class="navbar-drawer__link navbar-drawer__link--btn" @click="openContactChat">
            <span class="navbar-drawer__link-icon"><i class="mdi mdi-message-text" aria-hidden="true"></i></span>
            Contactează-ne
          </button>
        </template>
      </nav>
    </aside>

    <!-- Bara de navigare principală -->
    <header class="site-navbar" role="banner">
      <!-- Stânga: hamburger -->
      <button type="button" class="site-navbar__hamburger" @click="toggleDrawer" :aria-expanded="drawerOpen" :aria-label="drawerOpen ? 'Închide meniu' : 'Deschide meniu'">
        <span class="site-navbar__hamburger-line"></span>
        <span class="site-navbar__hamburger-line"></span>
        <span class="site-navbar__hamburger-line"></span>
      </button>

      <!-- Centru: titlul site-ului -->
      <router-link class="site-navbar__brand" :to="{ name: 'home' }">
        {{ appName }}
      </router-link>

      <!-- Dreapta: autentificare -->
      <div class="site-navbar__auth">
        <!-- Notification bell (când e autentificat) -->
        <div v-if="currentUser" class="site-navbar__bell">
          <NotificationBell :current-user="currentUser" />
        </div>

        <!-- Neautentificat: buton login -->
        <button v-if="!currentUser" type="button" class="site-navbar__login-btn" @click="openAuthModal">
          Intră în cont
        </button>

        <!-- Autentificat: avatar + dropdown -->
        <div v-else class="site-navbar__user" ref="userArea">
          <button type="button" class="site-navbar__avatar" @click="toggleUserDropdown" :aria-expanded="userDropdownOpen" :aria-label="`Meniu utilizator ${currentUser.username}`">
            {{ firstLetter }}
          </button>

          <div v-if="userDropdownOpen" class="site-navbar__user-dropdown" @click.stop>
            <div class="site-navbar__user-dropdown-name">{{ currentUser.username }}</div>
            <router-link class="site-navbar__user-dropdown-link" :to="{ name: 'profile' }" @click="userDropdownOpen = false">
              Date cont
            </router-link>
            <button type="button" class="site-navbar__user-dropdown-logout" @click="doLogout">
              Deconectare
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Modal de autentificare -->
    <AuthModal v-if="authModalOpen" :show-notice="false" @close="authModalOpen = false" @success="onAuthSuccess" />
  </div>
</template>

<script>
import AuthModal from "./AuthModal.vue";
import NotificationBell from "./NotificationBell.vue";

export default {
  name: "SiteNavbar",

  components: { AuthModal, NotificationBell },

  data() {
    return {
      appName: window.application?.name || "Astacred",
      currentUser: null,
      drawerOpen: false,
      userDropdownOpen: false,
      authModalOpen: false,
    };
  },

  computed: {
    firstLetter() {
      const name = this.currentUser?.username || this.currentUser?.name || "";
      return name.charAt(0).toUpperCase() || "?";
    },
  },

  async created() {
    await this.fetchCurrentUser();
  },

  mounted() {
    document.addEventListener("click", this.onDocumentClick);
    window.addEventListener("user-authenticated", this.onUserAuthenticated);
    window.addEventListener("user-logged-out", this.onUserLoggedOut);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClick);
    window.removeEventListener("user-authenticated", this.onUserAuthenticated);
    window.removeEventListener("user-logged-out", this.onUserLoggedOut);
  },

  watch: {
    $route() {
      this.drawerOpen = false;
      this.userDropdownOpen = false;
    },
  },

  methods: {
    async fetchCurrentUser() {
      const token = window.localStorage.getItem("auth_token");
      if (!token) return;
      try {
        const res = await window.axios.get("/auth/me");
        this.currentUser = res.data?.user || null;
      } catch {
        window.localStorage.removeItem("auth_token");
        delete window.axios.defaults.headers.common.Authorization;
        this.currentUser = null;
      }
    },

    toggleDrawer() {
      this.drawerOpen = !this.drawerOpen;
    },

    closeDrawer() {
      this.drawerOpen = false;
    },

    openAuthModal() {
      this.authModalOpen = true;
    },

    toggleUserDropdown() {
      this.userDropdownOpen = !this.userDropdownOpen;
    },

    onDocumentClick(e) {
      if (!this.userDropdownOpen) return;
      const area = this.$refs.userArea;
      if (area && !area.contains(e.target)) {
        this.userDropdownOpen = false;
      }
    },

    onAuthSuccess({ user }) {
      this.currentUser = user;
      this.authModalOpen = false;
      window.dispatchEvent(new CustomEvent("user-authenticated", { detail: user }));
    },

    async doLogout() {
      try {
        await window.axios.post("/auth/logout");
      } catch {
        // ignoră erori
      }
      window.localStorage.removeItem("auth_token");
      delete window.axios.defaults.headers.common.Authorization;
      this.currentUser = null;
      this.userDropdownOpen = false;
      window.dispatchEvent(new CustomEvent("user-logged-out"));
      this.$router.push({ name: "home" });
    },

    onUserAuthenticated(e) {
      this.currentUser = e.detail;
    },

    onUserLoggedOut() {
      this.currentUser = null;
    },

    openContactChat() {
      this.drawerOpen = false;
      window.dispatchEvent(new CustomEvent("open-contact-chat"));
    },
  },
};
</script>
